<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import {
    KsButton,
    KsDrawer,
    KsInput
  } from '@aschehoug/kloss'
  import { useAuthStore } from '@/stores/auth'
  import Typography from '@/components/common/Typography.vue'
  import { fetchCoreExternal } from '@/utils/request'
  import type { User } from '@/types/User'
  import { watch } from 'vue'
  import { router } from '@/router'

  defineProps<{ open: boolean }>()

  const emit = defineEmits(['close'])
  const { t } = useI18n()
  const auth = useAuthStore()

  watch(() => auth.user?.preferredLanguage, async (newLang: string | undefined) => {
    if (newLang)
      await updatePrefferedLanguage(newLang)
  })

  async function updatePrefferedLanguage(languageCode: string): Promise<void> {
    const payload: User | null = auth.user

    if (payload)
      payload.preferredLanguage = languageCode

    await fetchCoreExternal('/frontend/session/user', {
      method: 'PUT',
      body: JSON.stringify(payload),
    })

    router.go(0)
  }
</script>

<template>
  <KsDrawer
    :title="t('settings.title')"
    position="top"
    size="auto"
    :open="open"
    @close="emit('close')"
  >
    <template #body>
      <Typography class="settings-name">
        {{ auth.user!.fullName }}
      </Typography>

      <KsButton
        v-if="auth.isAuthenticated"
        class="logout"
        shape="rounded"
        variant="primary"
        @click="auth.logout"
      >
        {{ t('app.logout') }}
      </KsButton>

      <form>
        <fieldset class="settings-language">
          <legend v-text="t('settings.language')" />
          <KsButton
            variant="tertiary"
            shape="rounded"
            type="label"
          >
            <KsInput
              v-model="auth.user!.preferredLanguage"
              value="nb"
              type="radio"
            />
            {{ t('language.nb') }}
          </KsButton>
          <KsButton
            variant="tertiary"
            shape="rounded"
            type="label"
          >
            <KsInput
              v-model="auth.user!.preferredLanguage"
              value="nn"
              type="radio"
            />
            {{ t('language.nn') }}
          </KsButton>
        </fieldset>
      </form>
    </template>
  </KsDrawer>
</template>

<style lang="scss" scoped>
  :deep(h1) {
    @include h3;

    margin-bottom: 0;
  }

  :deep(h2) {
    @include body;

    font-weight: 500;
  }

  .logout {
    margin: 10px 0;

    @include vgs-media-mobile {
      display: flex;
    }
  }

  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
  }

  legend {
    padding: 0 0 .6em;
  }

  .settings-name {
    margin: 0 0 1em;
  }

  .settings-language {
    display: flex;
    gap: .6em;
    margin-top: 1em;
  }

</style>
